import { Link } from '@tanstack/react-router';
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  begin,
  move,
  offset,
  progress,
  settle,
  velocity,
  type Drag,
  type Side,
} from '../lib/drawerDrag';

/**
 * The tabs on a phone: behind a Menu button, in a panel that slides in from
 * the left edge, and that a finger can drag in from anywhere on the screen.
 *
 * A native `<dialog>` opened with `showModal()`. That is the first modal
 * surface in the app (the popovers are deliberately not), and it is used here
 * because the browser then does the whole job: the top layer, the focus trap,
 * Escape, the rest of the page made inert. Nothing here traps focus by hand.
 * The dialog covers the screen and holds two things, a scrim and the panel;
 * the reasons are with the `.drawer` styles.
 *
 * Only navigation lives here. Theme, switching organization and signing out
 * stay in the account menu at the top right, on a phone as on a desktop.
 *
 * ## The drag
 *
 * The panel follows the finger 1:1, the scrim darkens with it, and when the
 * finger lifts the panel settles on whichever side it was nearer, or the side
 * it was thrown towards. Dragging the open panel left closes it the same way.
 * The arithmetic is in lib/drawerDrag.ts; this is the listeners and the
 * inline styles.
 *
 * Five phases. `closed` renders nothing. `dragging` renders the dialog WITHOUT
 * opening it: the stylesheet shows a closed dialog in that phase as an
 * ordinary fixed box, and the panel and scrim take their position from inline
 * styles written on every move, through refs rather than state, because a
 * render per touchmove is not what a 60Hz gesture wants. `settling` is that
 * same closed box sliding back out of sight after a drag let go early. `open`
 * is modal, as the Menu button has always made it. `closing` is still modal
 * and sliding out after a drag, with `close()` waiting on the transition.
 *
 * Modality arrives at release, not during the drag, on purpose. Chromium was
 * measured to keep delivering a touch after `showModal()` made the page inert
 * under it, but that is exactly the kind of thing another engine might answer
 * with a touchcancel, and iOS is the phone this is for. A closed dialog that
 * merely happens to be displayed changes nothing about the page the finger is
 * on. And a drag that settles open eases from where the finger left it rather
 * than restarting from off-screen, because the inline position is cleared in
 * the same task that opens the dialog.
 *
 * The gesture itself lives in a ref. The listeners are installed per phase,
 * and the first move that locks the axis renders `dragging` synchronously
 * from inside a listener, which reinstalls them mid-gesture; a closure
 * variable would go with the old listeners, a ref survives.
 *
 * Deliberately not an edge swipe. The edge is not the page's to have:
 * Android's gesture navigation and iOS, in a tab and in an installed app
 * alike, take a swipe that begins on the bezel as Back before the page hears
 * of it, and nothing in web content can refuse them. So the drag begins
 * anywhere, and is refused only where a sideways drag already means
 * something: inside a box that scrolls sideways (the register), a text field
 * (selecting), or a drag handle (reordering). The button is the primary
 * control; the drag is the shortcut.
 */
type Phase = 'closed' | 'dragging' | 'settling' | 'open' | 'closing';

/**
 * How long, in ms, to wait for a settling or closing panel's transition
 * before giving up on the event and finishing anyway. Longer than the 200ms
 * transition, and a real fallback rather than a nicety: a panel let go where
 * it already was has no transition to end, and reduced motion makes every
 * transition so short that its end can be missed.
 */
const EXIT = 300;

export function NavDrawer({
  items,
  activeTo,
  heading,
  pathname,
}: {
  items: readonly { to: string; label: string }[];
  /**
   * The tab the shell counts as current. Not `Link`'s own notion of active,
   * which is a prefix match and lights Organizations as well as People on
   * /platform/people.
   */
  activeTo: string | null;
  /** The organization's name, or "Platform" outside one. */
  heading: string;
  /** Closes the drawer when it changes, however the change came about. */
  pathname: string;
}) {
  const [phase, setPhase] = useState<Phase>('closed');
  const trigger = useRef<HTMLButtonElement>(null);
  const dialog = useRef<HTMLDialogElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const scrim = useRef<HTMLDivElement>(null);
  const drag = useRef<Drag | null>(null);
  /** The identifier of the touch that owns the gesture; others are ignored. */
  const finger = useRef(0);
  /** The panel's width, measured once when a drag locks. */
  const width = useRef(0);
  const dialogId = useId();
  const headingId = useId();

  const open = phase === 'open';

  /*
   * What each phase does to the dialog, before paint. Opening clears the
   * inline position unconditionally: the Menu button can be pressed while
   * the panel is settling back, and a panel opened with its settling
   * position still on it would open out of sight. showModal() is guarded
   * because StrictMode runs effects twice and a second call throws; there is
   * no close() in the cleanup for the same reason.
   *
   * Settling and closing both send the panel out and wait for its
   * transition to end, or for EXIT to pass, whichever is first; they differ
   * only in what then happens. The wait lives in the effect so that a change
   * of phase from elsewhere (Escape, the scrim, a route, a widening) cancels
   * it, and so that the panel can be grabbed again while it is on its way.
   */
  useLayoutEffect(() => {
    const el = dialog.current;
    if (!el) return;
    if (phase === 'open') {
      drag.current = null;
      release(el, panel.current, scrim.current);
      if (!el.open) el.showModal();
      return;
    }
    if (phase !== 'settling' && phase !== 'closing') return;
    hide(el, panel.current, scrim.current, width.current);
    const done = phase === 'settling' ? () => setPhase('closed') : () => el.close();
    const onEnd = (event: TransitionEvent) => {
      if (event.target === panel.current) done();
    };
    const node = panel.current;
    node?.addEventListener('transitionend', onEnd);
    const timer = window.setTimeout(done, EXIT);
    return () => {
      node?.removeEventListener('transitionend', onEnd);
      window.clearTimeout(timer);
    };
  }, [phase]);

  /*
   * The dialog's own close event is the one source of truth for "closed":
   * Escape, the scrim, a tab, a drag, a widening viewport and a route change
   * all end up here. Focus goes back to the button explicitly. The browser
   * returns it to whatever had it before showModal(), which is this button on
   * most platforms, but Safari does not focus a button on click, so there it
   * would be the body.
   */
  const onClose = () => {
    setPhase('closed');
    trigger.current?.focus();
  };

  /*
   * Any change of address takes the drawer away, whatever it was doing: a
   * tab chosen here, or the browser's own Back. Adjusted during render rather
   * than in an effect, React's documented pattern for state that answers to
   * a prop (the register does the same for its date). A tab's click handler
   * closes the dialog properly first, which is what puts focus back on the
   * button; this is the guarantee behind it, and behind Back, where the page
   * has moved on and focus has nowhere in particular to be.
   */
  const [seenPathname, setSeenPathname] = useState(pathname);
  if (seenPathname !== pathname) {
    setSeenPathname(pathname);
    if (phase !== 'closed') setPhase('closed');
  }

  // A phone turned sideways, or a window widened: the tabs are back in the
  // masthead and the drawer has nothing to be, whatever it was doing.
  useEffect(() => {
    if (phase === 'closed') return;
    const wide = window.matchMedia('(min-width: 40rem)');
    const onChange = () => {
      if (!wide.matches) return;
      if (phase === 'dragging') setPhase('settling');
      else dialog.current?.close();
    };
    wide.addEventListener('change', onChange);
    return () => wide.removeEventListener('change', onChange);
  }, [phase]);

  /*
   * The touch pipeline, one for every phase, on the document. A touch on the
   * open dialog bubbles here too, so the closing drag needs nothing of its
   * own. Passive: nothing here prevents a scroll, it only watches; a drag
   * that leans may scroll the page a little while the panel follows.
   *
   * Reinstalled on every phase, which the first locking move triggers from
   * inside a handler: fine, because a listener removed during its own
   * dispatch still finishes, and the gesture is in a ref that outlives it.
   * Out of `dragging`, the ref is cleared first, so a gesture that a phase
   * change overtook never writes into a dialog that is on its way out.
   */
  useEffect(() => {
    if (phase !== 'dragging') drag.current = null;
    if (phase === 'closing') return;
    const from: Side = phase === 'open' ? 'open' : 'closed';
    const wide = window.matchMedia('(min-width: 40rem)');
    const sample = (touch: Touch, event: TouchEvent) => ({
      x: touch.clientX,
      y: touch.clientY,
      t: event.timeStamp,
    });
    const owned = (list: TouchList) =>
      Array.from(list).find((touch) => touch.identifier === finger.current);

    const onStart = (event: TouchEvent) => {
      if (drag.current) return;
      const touch = event.changedTouches[0];
      if (!touch) return;
      if (from === 'closed' && (wide.matches || spokenFor(event.target))) return;
      finger.current = touch.identifier;
      drag.current = begin(from, sample(touch, event));
    };

    const onMove = (event: TouchEvent) => {
      const current = drag.current;
      const touch = owned(event.changedTouches);
      if (!current || !touch) return;
      const next = move(current, sample(touch, event));
      if (next.axis === 'none') {
        drag.current = null;
        return;
      }
      drag.current = next;
      if (next.axis !== 'horizontal') return;

      if (current.axis !== 'horizontal') {
        // The move that locked. The panel has to exist, and be laid out, before
        // it can follow: flushSync, because a state change from a raw
        // listener would otherwise commit some time after the next touchmove.
        if (from === 'closed') flushSync(() => setPhase('dragging'));
        else dialog.current?.setAttribute('data-dragging', '');
        const measured = panel.current?.offsetWidth ?? 0;
        if (!(measured > 0)) {
          drag.current = null;
          if (from === 'closed') setPhase('closed');
          return;
        }
        width.current = measured;
      }

      // The panel's edge is where the finger has put it, clamped to the panel:
      // in from hidden while opening, out from its place while closing.
      const dx = offset(next);
      const shift = Math.max(
        -width.current,
        Math.min(0, from === 'closed' ? dx - width.current : dx),
      );
      panel.current?.style.setProperty('translate', `${shift}px 0`);
      scrim.current?.style.setProperty('opacity', String(progress(from, dx, width.current)));
    };

    const onEnd = (event: TouchEvent) => {
      const current = drag.current;
      const touch = owned(event.changedTouches);
      if (!current || !touch) return;
      drag.current = null;
      if (current.axis !== 'horizontal') return;
      const side = settle(
        progress(from, offset(current), width.current),
        velocity(current, event.timeStamp),
      );
      if (from === 'closed') setPhase(side === 'open' ? 'open' : 'settling');
      else if (side === 'closed') setPhase('closing');
      else release(dialog.current, panel.current, scrim.current);
    };

    // The browser took the touch for itself (a scroll it decided on, a
    // system gesture): whatever was dragged goes back where it was.
    const onCancel = (event: TouchEvent) => {
      const current = drag.current;
      if (!current || !owned(event.changedTouches)) return;
      drag.current = null;
      if (current.axis !== 'horizontal') return;
      if (from === 'closed') setPhase('settling');
      else release(dialog.current, panel.current, scrim.current);
    };

    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchmove', onMove, { passive: true });
    document.addEventListener('touchend', onEnd, { passive: true });
    document.addEventListener('touchcancel', onCancel, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onCancel);
    };
  }, [phase]);

  return (
    <>
      <button
        ref={trigger}
        type="button"
        className="-ml-3 grid size-11 shrink-0 place-items-center rounded text-ink-soft hover:bg-paper-sunk hover:text-ink sm:hidden"
        aria-label="Menu"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? dialogId : undefined}
        onClick={() => setPhase('open')}
      >
        {/* Drawn, not a glyph, for the reason the theme icons are (UserMenu). */}
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          aria-hidden="true"
          focusable="false"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
        >
          <path d="M3 5h14M3 10h14M3 15h14" />
        </svg>
      </button>
      {phase !== 'closed' ? (
        <dialog
          ref={dialog}
          id={dialogId}
          className="drawer"
          data-phase={phase}
          aria-labelledby={headingId}
          onClose={onClose}
          // A press anywhere but the panel closes: the scrim, or whatever of
          // the dialog's own box neither covers.
          onClick={(event) => {
            if (!panel.current?.contains(event.target as Node)) event.currentTarget.close();
          }}
        >
          {/* The scrim first, so the panel paints above it: both are
              positioned, and DOM order is what settles that. */}
          <div ref={scrim} className="drawer-scrim" />
          <div ref={panel} className="drawer-panel grid content-start gap-1 p-3">
            <h2 id={headingId} className="flex items-baseline gap-3 px-3 py-2">
              <span className="shrink-0 font-mono text-[13px] font-semibold tracking-[0.18em] uppercase">
                Quire
              </span>
              <span className="truncate text-sm font-normal text-ink-soft">{heading}</span>
            </h2>
            <nav>
              <ul className="grid gap-0.5">
                {items.map((item) => {
                  const active = item.to === activeTo;
                  return (
                    <li key={item.to}>
                      <Link
                        to={item.to}
                        className="flex min-h-11 items-center rounded px-3 text-sm"
                        style={{
                          background: active ? 'var(--color-paper-sunk)' : 'transparent',
                          color: active ? 'var(--color-ink)' : 'var(--color-ink-soft)',
                          fontWeight: active ? 600 : 400,
                        }}
                        onClick={() => dialog.current?.close()}
                      >
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>
          </div>
        </dialog>
      ) : null}
    </>
  );
}

/** Hands the panel back to the stylesheet, which eases it to its place. */
function release(
  dialog: HTMLDialogElement | null,
  panel: HTMLElement | null,
  scrim: HTMLElement | null,
) {
  dialog?.removeAttribute('data-dragging');
  panel?.style.removeProperty('translate');
  scrim?.style.removeProperty('opacity');
}

/** Sends the panel out of sight, easing from wherever it is. */
function hide(
  dialog: HTMLDialogElement | null,
  panel: HTMLElement | null,
  scrim: HTMLElement | null,
  width: number,
) {
  dialog?.removeAttribute('data-dragging');
  panel?.style.setProperty('translate', `${-width}px 0`);
  scrim?.style.setProperty('opacity', '0');
}

/**
 * Whether a touch at `target` began on something that already has a use for
 * a sideways drag. A box that scrolls sideways is scrolled by it: the
 * register is dragged by its marks, and its name column, sticky at the left
 * edge, would otherwise be an opening drag waiting to happen. A text field
 * selects with it, and the roster's drag handle is a drag by definition.
 */
function spokenFor(target: EventTarget | null): boolean {
  const el = target instanceof Element ? target : null;
  if (el?.closest('input, textarea, select, .grip')) return true;
  for (let box = el; box; box = box.parentElement) {
    const overflow = getComputedStyle(box).overflowX;
    if ((overflow === 'auto' || overflow === 'scroll') && box.scrollWidth > box.clientWidth) {
      return true;
    }
  }
  return false;
}
